/*
 * Copyright (c) 2024 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { useCallback, useMemo, useState } from "react";
import {
    AuthFlowType,
    ChallengeNameType,
    CognitoIdentityProviderClient,
    InitiateAuthCommand,
    RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ViaToastProps } from "via-react-design-system";
import Cookies from "js-cookie";
import { useIntl } from "react-intl";

import { useLazyDriverIdQuery } from "store/api/authenticationApiSlice";
import { DRM_DRIVER_ID_API_URL } from "store/api/constants";
import { configurationSlice } from "store/slices/configuration";
import {
    ACCESS_TOKEN,
    COGNITO_CLIENT_ID,
    DRIVER_SESSION,
    ID_TOKEN,
    REFRESH_TOKEN,
} from "pages/LoginWeb/constants";
import { useCommonState } from "common/hooks";

import { logAnalyticsEvent } from "common/analytics";
import AnalyticsEventName from "common/analytics/types/AnalyticsEventName";

import { messages } from "../messages";
import { setCookie } from "../utils";

interface ErrorType extends ViaToastProps {
    message?: string;
}

export const useLogin = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType | null>(null);
    const [driverId, setDriverId] = useState<string>("");
    const [userPhoneNumber, setUserPhoneNumber] = useState<string>("");

    // const authConfigs = useAuthConfigs();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const intl = useIntl();

    const {
        configuration: { authConfiguration },
    } = useCommonState();

    const cognitoClient = useMemo(
        () =>
            new CognitoIdentityProviderClient({
                region: authConfiguration.region,
            }),
        [authConfiguration.region]
    );

    const [getDriverId] = useLazyDriverIdQuery();

    const onRequestSms = useCallback(
        async (userId?: string) => {
            setLoading(true);
            setError(null);

            try {
                // Request sms code
                const respondToAuthChallenge = new RespondToAuthChallengeCommand({
                    ChallengeName: ChallengeNameType.CUSTOM_CHALLENGE,
                    ChallengeResponses: {
                        ANSWER: "SEND_CODE_BY_SMS",
                        USERNAME: userId || driverId,
                    },
                    ClientId: authConfiguration.cognitoClientId,
                    Session: Cookies.get(DRIVER_SESSION) ?? "",
                    ClientMetadata: {
                        auth_challenge_type: "PROVIDE_AUTH_TYPE",
                        auth_type: "PASSWORDLESS",
                        comm_type: "SMS",
                    },
                });

                const respondToAuthChallengeResponse = await cognitoClient.send(
                    respondToAuthChallenge
                );
                const { Session: respondToAuthChallengeSession } = respondToAuthChallengeResponse;
                if (respondToAuthChallengeSession) {
                    setCookie({ name: DRIVER_SESSION, value: respondToAuthChallengeSession });
                }
                setLoading(false);
            } catch (error) {
                setLoading(false);
                setError({
                    open: true,
                    type: "error",
                    message: String(error),
                });
            }
        },
        [authConfiguration.cognitoClientId, cognitoClient, driverId]
    );

    const onRequestCodeByPhone = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Request sms code
            const respondToAuthChallenge = new RespondToAuthChallengeCommand({
                ChallengeName: ChallengeNameType.CUSTOM_CHALLENGE,
                ChallengeResponses: {
                    ANSWER: "SEND_CODE_BY_PHONE_CALL",
                    USERNAME: driverId,
                },
                ClientId: authConfiguration.cognitoClientId,
                Session: Cookies.get(DRIVER_SESSION) ?? "",
                ClientMetadata: {
                    auth_challenge_type: "PROVIDE_AUTH_TYPE",
                    auth_type: "PASSWORDLESS",
                    comm_type: "PHONE_CALL",
                },
            });

            const respondToAuthChallengeResponse = await cognitoClient.send(respondToAuthChallenge);
            const { Session: respondToAuthChallengeSession } = respondToAuthChallengeResponse;
            if (respondToAuthChallengeSession) {
                setCookie({ name: DRIVER_SESSION, value: respondToAuthChallengeSession });
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);

            setError({
                open: true,
                type: "error",
                message: String(error),
            });
        }
    }, [authConfiguration.cognitoClientId, cognitoClient, driverId]);

    const onPhoneSubmit = useCallback(
        async (countryCode: string, phoneNumber: string) => {
            setLoading(true);
            setError(null);
            setUserPhoneNumber(`+${countryCode}${phoneNumber}`);

            try {
                // Get DRM driverId by phone number
                const driverResponse = await getDriverId({
                    url: `${DRM_DRIVER_ID_API_URL}${countryCode}${phoneNumber}&active_only=true`,
                });
                const driverDrm = driverResponse.data?.[0] || {};
                const drmDriverId = driverDrm.drmId;
                setDriverId(drmDriverId);
                dispatch(
                    configurationSlice.actions.setDrmDriver({
                        id: drmDriverId,
                    })
                );

                // Init authentication
                const initiateAuth = new InitiateAuthCommand({
                    AuthFlow: AuthFlowType.CUSTOM_AUTH,
                    AuthParameters: {
                        USERNAME: driverDrm.drmId,
                    },
                    ClientId: authConfiguration.cognitoClientId,
                });
                const initiateAuthResult = await cognitoClient.send(initiateAuth);
                const { Session } = initiateAuthResult;
                if (Session) {
                    setCookie({ name: DRIVER_SESSION, value: Session });
                    setCookie({
                        name: COGNITO_CLIENT_ID,
                        value: authConfiguration.cognitoClientId,
                    });
                }

                await onRequestSms(drmDriverId);

                logAnalyticsEvent({
                    info: {
                        errorMessage: "",
                        flowName: "login",
                        flowPlatform: "browser",
                        flowSubsection: "login_init",
                        isError: false,
                        payload: { phoneNumber: `${countryCode}${phoneNumber}` },
                    },
                    name: AnalyticsEventName.UserAuthentication,
                });
            } catch (error) {
                setLoading(false);
                setError({
                    open: true,
                    type: "error",
                    message: String(error),
                });

                logAnalyticsEvent({
                    info: {
                        errorMessage: String(error),
                        flowName: "login",
                        flowPlatform: "browser",
                        flowSubsection: "login_init",
                        isError: true,
                        payload: { phoneNumber: `${countryCode}${phoneNumber}` },
                    },
                    name: AnalyticsEventName.UserAuthentication,
                });
            }
        },
        [
            authConfiguration.cognitoClientId,
            cognitoClient,
            dispatch,
            getDriverId,
            onRequestSms,
        ]
    );

    const onSubmitSmsCode = useCallback(
        async (code) => {
            setLoading(true);

            try {
                const respondToAuthChallenge = new RespondToAuthChallengeCommand({
                    ChallengeName: ChallengeNameType.CUSTOM_CHALLENGE,
                    ChallengeResponses: {
                        ANSWER: code,
                        USERNAME: driverId,
                    },
                    ClientId: authConfiguration.cognitoClientId,
                    Session: Cookies.get(DRIVER_SESSION) ?? "",
                    ClientMetadata: {
                        auth_challenge_type: "PROVIDE_ONE_TIME_PASSWORD",
                        one_time_password: code,
                    },
                });

                const respondToAuthChallengeResponse = await cognitoClient.send(
                    respondToAuthChallenge
                );
                const { AuthenticationResult, ChallengeParameters } =
                    respondToAuthChallengeResponse;
                if (ChallengeParameters?.error_key) {
                    // Handle wrong code error
                    setError({
                        open: true,
                        message: intl.formatMessage(messages.pendingCancelation),
                        type: "error",
                        autoHideDuration: 100000,
                        actions: [
                            {
                                text: "Get new code",
                                handler: () => onRequestSms(),
                            },
                        ],
                    });
                    setLoading(false);
                    if (respondToAuthChallengeResponse.Session) {
                        setCookie({
                            name: DRIVER_SESSION,
                            value: respondToAuthChallengeResponse.Session,
                        });
                    }
                }
                if (
                    AuthenticationResult?.AccessToken &&
                    AuthenticationResult.IdToken &&
                    AuthenticationResult.RefreshToken
                ) {
                    const { AccessToken, IdToken, RefreshToken } = AuthenticationResult;
                    setCookie({ name: ACCESS_TOKEN, value: AccessToken });
                    setCookie({ name: ID_TOKEN, value: IdToken });
                    setCookie({ name: REFRESH_TOKEN, value: RefreshToken });

                    dispatch(configurationSlice.actions.setAuthToken(IdToken));
                    setLoading(false);

                    logAnalyticsEvent({
                        info: {
                            errorMessage: "",
                            flowName: "login",
                            flowPlatform: "browser",
                            flowSubsection: "login_init",
                            isError: false,
                            payload: { phoneNumber: userPhoneNumber },
                        },
                        name: AnalyticsEventName.UserAuthentication,
                    });

                    navigate("/schedule");
                }
            } catch (error) {
                setLoading(false);
                setError({
                    open: true,
                    type: "error",
                    message: String(error),
                });

                logAnalyticsEvent({
                    info: {
                        errorMessage: String(error),
                        flowName: "login",
                        flowPlatform: "browser",
                        flowSubsection: "login_confirm",
                        isError: true,
                        payload: { phoneNumber: userPhoneNumber },
                    },
                    name: AnalyticsEventName.UserAuthentication,
                });
            }
        },
        [
            authConfiguration.cognitoClientId,
            cognitoClient,
            dispatch,
            driverId,
            intl,
            navigate,
            onRequestSms,
            userPhoneNumber,
        ]
    );

    const onClearError = useCallback(() => setError(null), []);

    return {
        driverId,
        error,
        loading,
        onPhoneSubmit,
        onRequestSms,
        onSubmitSmsCode,
        onRequestCodeByPhone,
        onClearError,
    };
};
